/* ============================================================
   Wido Ops — auth flow: Google SSO + pending activation gate.
   Exports window.AuthFlow({ onAuthed }).
   ============================================================ */
(function () {
  const { useState, useEffect } = React;
  const D = window.WidoData;
  const USE_API = !!window.WIDO_USE_API;

  // dev-users has no email; synthesize a display address from the name.
  function emailFor(u) {
    if (u.email) return u.email;
    const parts = String(u.name || "").toLowerCase().normalize("NFD")
      .replace(/[^a-z ]/g, "").trim().split(/\s+/);
    return (parts.slice(-2).join(".") || "user") + "@widogame.com";
  }

  function GoogleG({ size = 20 }) {
    return React.createElement("svg", { className: "gicon", width: size, height: size, viewBox: "0 0 48 48" },
      React.createElement("path", { fill: "#EA4335", d: "M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" }),
      React.createElement("path", { fill: "#4285F4", d: "M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" }),
      React.createElement("path", { fill: "#FBBC05", d: "M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" }),
      React.createElement("path", { fill: "#34A853", d: "M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" }),
    );
  }

  const ACCOUNTS = [
    { person: D.helpers.byId("u4"), email: "bao.dg@widogame.com" },          // active employee
    { person: D.people.find((p) => p.roleId === "hr"), email: "hong.nt@widogame.com" }, // HR
    { person: D.people.find((p) => p.status === "pending"), email: "phong.tg@widogame.com" }, // pending
    { external: true, name: "Bao Do (personal)", email: "dogiabao.92@gmail.com" },
  ];

  function avatarColor(name) {
    const palette = ["#2a7d36", "#cf4820", "#3a9a45", "#aa3411", "#14512a", "#ef5b2e"];
    let h = 0; for (const c of name) h = (h * 31 + c.charCodeAt(0)) >>> 0;
    return palette[h % palette.length];
  }
  function initials(name) { return name.split(" ").slice(-2).map((w) => w[0]).join("").toUpperCase(); }

  function AuthFlow({ onAuthed }) {
    const [stage, setStage] = useState("login"); // login | choose | pending | denied
    const [pendingAcct, setPendingAcct] = useState(null);
    const [denied, setDenied] = useState(null);
    const [liveAccounts, setLiveAccounts] = useState([]);
    const [busy, setBusy] = useState(USE_API); // true while checking the session on mount
    const [err, setErr] = useState(null);

    // API mode: resume an existing session, else load the seeded users to pick from.
    useEffect(() => {
      if (!USE_API) return;
      let alive = true;
      (async () => {
        try {
          // loadCritical() already fetches /api/me as part of its batch and
          // returns the person, so call it directly instead of a separate,
          // serial /api/me probe first (that doubled the call and added a whole
          // round-trip before the batch). No session -> it 401s and we fall
          // through to the chooser. The heavy company-wide calls stream in
          // afterward (app kicks off loadDeferred post-auth), so we land on the
          // dashboard fast instead of freezing on "Connecting…".
          const me = await window.WidoBootstrap.loadCritical();
          if (alive) { onAuthed(me); return; }
        } catch (e) { /* 401 -> show the chooser */ }
        // Only probe dev-login when this build enables it (dev). In production
        // (WIDO_DEV_LOGIN off) we skip the call entirely — no chooser, no 404.
        try {
          if (!window.WIDO_DEV_LOGIN) throw { status: 404 };
          const users = await window.WidoApi.devUsers();
          if (alive) setLiveAccounts(users.map((u) => ({ person: u, email: emailFor(u) })));
        } catch (e) {
          // 404 = dev-login is disabled (the normal production case): not an error —
          // fall through to Google Workspace SSO with no seeded accounts to pick.
          // Only a real connectivity failure (network error / 5xx) is worth showing.
          if (alive && e && e.status !== 404 && e.status !== 401) {
            setErr("Couldn't reach the backend. Check that the backend container is up and connected to the database.");
          }
        }
        if (alive) setBusy(false);
      })();
      return () => { alive = false; };
    }, []);

    async function chooseAccount(acct) {
      if (acct.external || !acct.email.endsWith("@widogame.com")) {
        setDenied(acct); setStage("denied"); return;
      }
      if (!USE_API) {
        if (acct.person.status === "pending") { setPendingAcct(acct); setStage("pending"); return; }
        onAuthed(acct.person); return;
      }
      // API mode: real dev-login + live bootstrap.
      setBusy(true); setErr(null);
      try {
        const person = await window.WidoApi.devLogin(acct.person.id);
        if (person.status === "pending") {
          setPendingAcct({ ...acct, person }); setStage("pending"); setBusy(false); return;
        }
        await window.WidoBootstrap.loadCritical();
        onAuthed(person);
      } catch (e) {
        if (e && e.status === 403) { setDenied(acct); setStage("denied"); }
        else setErr("Sign-in failed: " + ((e && e.message) || e));
        setBusy(false);
      }
    }

    const accountList = USE_API ? liveAccounts : ACCOUNTS;

    return React.createElement("div", { className: "auth" },
      // ---- brand panel ----
      React.createElement("div", { className: "auth__brandpanel" },
        React.createElement("span", { className: "material-symbols-rounded auth__watermark" }, "schedule"),
        React.createElement("div", { className: "row", style: { gap: 12, zIndex: 1 } },
          React.createElement("div", { className: "brandmark" }, React.createElement("span", { className: "material-symbols-rounded" }, "sports_esports")),
          React.createElement("span", { className: "wordmark" }, "widogame"),
        ),
        React.createElement("div", { style: { zIndex: 1 } },
          React.createElement("div", { className: "wordmark wordmark--lg", style: { marginBottom: 16 } }, "Wido Ops"),
          React.createElement("p", { style: { font: "400 20px/1.5 var(--font-sans)", color: "rgba(255,255,255,.78)", maxWidth: 420, margin: 0 } },
            "Make every working minute count — attendance, time off and approvals that run themselves."),
        ),
        React.createElement("div", { style: { zIndex: 1, display: "flex", gap: 28, color: "rgba(255,255,255,.6)", font: "400 14px/1.4 var(--font-sans)" } },
          React.createElement("span", null, "Secured by Google Workspace SSO"),
          React.createElement("span", null, "@widogame.com only"),
        ),
      ),
      // ---- form panel ----
      React.createElement("div", { className: "auth__formpanel" },
        React.createElement("div", { className: "auth__card" },
          stage === "login" && React.createElement(React.Fragment, null,
            React.createElement("div", null,
              React.createElement("h1", { style: { font: "400 32px/1.2 var(--font-sans)", margin: "0 0 8px" } }, "Sign in"),
              React.createElement("p", { className: "muted", style: { margin: 0, fontSize: 15 } }, "Use your Widogame Workspace account to continue."),
            ),
            React.createElement("button", { className: "gbtn", disabled: USE_API && busy, onClick: () => {
              // Dev-login available (seeded chooser) -> pick an account; otherwise
              // (production, DEV_LOGIN off) start real Google Workspace SSO.
              if (USE_API && !busy && liveAccounts.length === 0) {
                window.location.href = (import.meta.env.VITE_API_BASE_URL || "") + "/api/auth/login";
              } else {
                setStage("choose");
              }
            } },
              React.createElement(GoogleG, null), USE_API && busy ? "Connecting…" : "Sign in with Google"),
            err && React.createElement("div", { className: "banner", style: { background: "var(--md-error-container)", color: "var(--md-on-error-container)" } },
              React.createElement("span", { className: "material-symbols-rounded" }, "error"),
              React.createElement("span", { style: { fontSize: 13, lineHeight: 1.5 } }, err)),
            React.createElement("div", { className: "banner", style: { background: "var(--md-surface-container)", color: "var(--md-on-surface-variant)" } },
              React.createElement("span", { className: "material-symbols-rounded" }, "info"),
              React.createElement("span", { style: { fontSize: 13, lineHeight: 1.5 } },
                "New accounts must be activated by HR after first sign-in. Accounts removed from Google Workspace are deactivated automatically."),
            ),
          ),
          stage === "choose" && React.createElement(React.Fragment, null,
            React.createElement("div", { className: "row", style: { gap: 10 } },
              React.createElement(GoogleG, { size: 24 }),
              React.createElement("span", { style: { font: "400 22px/1 var(--font-sans)" } }, "Choose an account"),
            ),
            React.createElement("p", { className: "muted", style: { margin: "-8px 0 0", fontSize: 14 } }, "to continue to Wido Ops"),
            React.createElement("div", { className: "stack-v", style: { gap: 8 } },
              (USE_API && accountList.length === 0)
                ? React.createElement("p", { className: "muted", style: { fontSize: 14 } }, busy ? "Loading accounts…" : "No accounts found.")
                : null,
              accountList.map((a, i) => {
                const name = a.external ? a.name : a.person.name;
                return React.createElement("button", { key: i, className: "gaccount", onClick: () => chooseAccount(a) },
                  React.createElement("div", { className: "avatar", style: { background: avatarColor(name) } }, initials(name)),
                  React.createElement("div", { className: "stack-v", style: { gap: 2, minWidth: 0 } },
                    React.createElement("span", { style: { font: "500 15px/1.2 var(--font-sans)" } }, name),
                    React.createElement("span", { className: "muted", style: { fontSize: 13 } }, a.email),
                  ),
                  !a.external && a.person.status === "active" &&
                    React.createElement("span", { className: "tag tag--ok", style: { marginLeft: "auto" } }, "Active"),
                  !a.external && a.person.status === "pending" &&
                    React.createElement("span", { className: "tag tag--warn", style: { marginLeft: "auto" } }, "Pending"),
                );
              }),
            ),
            React.createElement("button", { className: "btn btn--text", style: { alignSelf: "flex-start" }, onClick: () => setStage("login") },
              React.createElement("span", { className: "material-symbols-rounded" }, "arrow_back"), "Back"),
          ),
          stage === "denied" && React.createElement(React.Fragment, null,
            React.createElement("div", { className: "stat__icon", style: { background: "var(--md-error-container)", width: 56, height: 56, borderRadius: 16 } },
              React.createElement("span", { className: "material-symbols-rounded", style: { color: "var(--md-on-error-container)", fontSize: 30 } }, "block")),
            React.createElement("div", null,
              React.createElement("h1", { style: { font: "400 26px/1.2 var(--font-sans)", margin: "0 0 8px" } }, "Domain not allowed"),
              React.createElement("p", { className: "muted", style: { margin: 0, fontSize: 15, lineHeight: 1.5 } },
                React.createElement("span", { className: "mono", style: { color: "var(--md-on-surface)" } }, denied && denied.email),
                " is not a Widogame Workspace account. Only ",
                React.createElement("strong", null, "@widogame.com"), " addresses can sign in."),
            ),
            React.createElement("button", { className: "btn btn--filled", onClick: () => setStage("choose") }, "Try another account"),
          ),
          stage === "pending" && React.createElement(React.Fragment, null,
            React.createElement("div", { className: "stat__icon", style: { background: "var(--md-tertiary-container)", width: 56, height: 56, borderRadius: 16 } },
              React.createElement("span", { className: "material-symbols-rounded", style: { color: "var(--md-on-tertiary-container)", fontSize: 30 } }, "hourglass_top")),
            React.createElement("div", null,
              React.createElement("h1", { style: { font: "400 26px/1.2 var(--font-sans)", margin: "0 0 8px" } }, "Waiting for activation"),
              React.createElement("p", { className: "muted", style: { margin: 0, fontSize: 15, lineHeight: 1.55 } },
                "You're signed in as ",
                React.createElement("strong", { style: { color: "var(--md-on-surface)" } }, pendingAcct && pendingAcct.person.name),
                ". Your account is verified but not yet activated. HR will review and activate it shortly — you'll get an email when access is ready."),
            ),
            React.createElement("div", { className: "kv", style: { padding: "8px 0" } },
              React.createElement("div", { className: "kv__row" }, React.createElement("span", { className: "kv__k" }, "Email"), React.createElement("span", { className: "kv__v mono", style: { fontSize: 14 } }, pendingAcct && pendingAcct.email)),
              React.createElement("div", { className: "kv__row" }, React.createElement("span", { className: "kv__k" }, "Status"), React.createElement("span", { className: "tag tag--warn" }, React.createElement("span", { className: "material-symbols-rounded" }, "schedule"), "Pending HR review")),
            ),
            React.createElement("div", { className: "stack-v", style: { gap: 10 } },
              !USE_API && React.createElement("button", { className: "btn btn--tonal", onClick: () => onAuthed(pendingAcct.person, true) },
                React.createElement("span", { className: "material-symbols-rounded" }, "bolt" ), "Simulate HR activation (demo)"),
              React.createElement("button", { className: "btn btn--text", onClick: () => setStage("choose") }, "Use another account"),
            ),
          ),
        ),
      ),
    );
  }

  window.AuthFlow = AuthFlow;
  window.WidoAvatar = { color: avatarColor, initials };
})();
